<template>
  <div class="video-upload-container">
    <div class="upload-area" :class="{ 'dragging': isDragging }">
      <!-- File Input -->
      <input
        ref="fileInput"
        type="file"
        accept="video/mp4,video/mpeg,video/quicktime"
        @change="handleFileSelect"
        style="display: none"
      />

      <!-- Upload Area -->
      <div
        v-if="!selectedFile && !isUploading"
        class="upload-zone"
        @click="triggerFileSelect"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleFileDrop"
      >
        <div class="upload-icon">📹</div>
        <p>Click here or drag and drop your MP4 video</p>
        <p class="upload-hint">Maximum file size: 500MB</p>
      </div>

      <!-- File Selected -->
      <div v-if="selectedFile && !isUploading" class="file-selected">
        <div class="file-info">
          <h4>{{ selectedFile.name }}</h4>
          <p>Size: {{ formatFileSize(selectedFile.size) }}</p>
          <p>Type: {{ selectedFile.type }}</p>
        </div>
        
        <div class="folder-selection">
          <label>Upload to folder:</label>
          <select v-model="selectedFolder">
            <option value="post">Post</option>
            <option value="products">Products</option>
            <option value="banners">Banners</option>
            <option value="chat/eventAvatar">Event Avatar</option>
            <option value="gallery">Gallery</option>
            <option value="game_grounds">Game Grounds</option>
            <option value="chat/groupAvatar">Group Avatar</option>
            <option value="score_sheets">Score Sheets</option>
            <option value="user_tasks">User Tasks</option>
          </select>
        </div>

        <div class="upload-actions">
          <button @click="startUpload" class="btn-upload" :disabled="isUploading">
            Upload Video
          </button>
          <button @click="clearSelection" class="btn-cancel">
            Cancel
          </button>
        </div>
      </div>

      <!-- Upload Progress -->
      <div v-if="isUploading" class="upload-progress">
        <div class="progress-info">
          <h4>Uploading {{ selectedFile.name }}...</h4>
          <div class="progress-bar">
            <div 
              class="progress-fill" 
              :style="{ width: uploadProgress + '%' }"
            ></div>
          </div>
          <p>{{ uploadProgress }}% completed</p>
          <p v-if="uploadSpeed">Speed: {{ uploadSpeed }}</p>
        </div>
        
        <button @click="cancelUpload" class="btn-cancel">
          Cancel Upload
        </button>
      </div>

      <!-- Upload Success -->
      <div v-if="uploadedFile" class="upload-success">
        <div class="success-icon">✅</div>
        <h4>Upload Successful!</h4>
        <p><strong>File:</strong> {{ uploadedFile.name }}</p>
        <p><strong>URL:</strong></p>
        <input 
          type="text" 
          :value="uploadedFile.url" 
          readonly 
          class="url-input"
          @click="$event.target.select()"
        />
        <div class="success-actions">
          <button @click="copyToClipboard(uploadedFile.url)" class="btn-copy">
            Copy URL
          </button>
          <button @click="resetUpload" class="btn-new">
            Upload Another
          </button>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="error-message">
        <p>❌ {{ errorMessage }}</p>
        <button @click="clearError" class="btn-clear">Clear</button>
      </div>
    </div>
  </div>
</template>

<script>
import AWS from 'aws-sdk'

export default {
  name: 'VideoUploade',
  data() {
    return {
      selectedFile: null,
      selectedFolder: 'post',
      isDragging: false,
      isUploading: false,
      uploadProgress: 0,
      uploadSpeed: '',
      uploadedFile: null,
      errorMessage: '',
      uploadStartTime: null,
      s3: null,
      abortController: null,
      
      // AWS Configuration
      awsConfig: {
        accessKeyId: 'AKIA5UYHAAMVN233RGRH',
        secretAccessKey: '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',
        region: 'us-west-2',
        bucket: 'whoifollow'
      },

      // Folder URL mappings
      folderUrls: {
        'post': 'https://whoifollow.s3.us-west-2.amazonaws.com/post/',
        'products': 'https://whoifollow.s3.us-west-2.amazonaws.com/products/',
        'banners': 'https://whoifollow.s3.us-west-2.amazonaws.com/banners/',
        'chat/eventAvatar': 'https://whoifollow.s3.us-west-2.amazonaws.com/chat/eventAvatar/',
        'gallery': 'https://whoifollow.s3.us-west-2.amazonaws.com/gallery/',
        'game_grounds': 'https://whoifollow.s3.us-west-2.amazonaws.com/game_grounds/',
        'chat/groupAvatar': 'https://whoifollow.s3.us-west-2.amazonaws.com/chat/groupAvatar/',
        'score_sheets': 'https://whoifollow.s3.us-west-2.amazonaws.com/score_sheets/',
        'user_tasks': 'https://whoifollow.s3.us-west-2.amazonaws.com/user_tasks/'
      }
    }
  },

  mounted() {
    this.initializeAWS()
  },

  methods: {
    initializeAWS() {
      AWS.config.update({
        accessKeyId: this.awsConfig.accessKeyId,
        secretAccessKey: this.awsConfig.secretAccessKey,
        region: this.awsConfig.region
      })

      this.s3 = new AWS.S3({
        params: {
          Bucket: this.awsConfig.bucket
        }
      })
    },

    triggerFileSelect() {
      this.$refs.fileInput.click()
    },

    handleFileSelect(event) {
      const file = event.target.files[0]
      this.validateAndSetFile(file)
    },

    handleFileDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      this.validateAndSetFile(file)
    },

    validateAndSetFile(file) {
      if (!file) return

      // Validate file type
      const allowedTypes = ['video/mp4', 'video/mpeg', 'video/quicktime']
      if (!allowedTypes.includes(file.type)) {
        this.errorMessage = 'Please select a valid video file (MP4, MPEG, or QuickTime)'
        return
      }

      // Validate file size (500MB max)
      const maxSize = 500 * 1024 * 1024
      if (file.size > maxSize) {
        this.errorMessage = 'File size must be less than 500MB'
        return
      }

      this.selectedFile = file
      this.clearError()
    },

    generateUniqueFileName(originalName) {
      const timestamp = Date.now()
      const random = Math.random().toString(36).substring(7)
      const extension = originalName.split('.').pop()
      const nameWithoutExt = originalName.replace(/\.[^/.]+$/, "")
      
      return `${timestamp}_${random}_${nameWithoutExt}.${extension}`
    },

    async startUpload() {
      if (!this.selectedFile) return

      this.isUploading = true
      this.uploadProgress = 0
      this.uploadStartTime = Date.now()
      this.abortController = new AbortController()

      try {
        const uniqueFileName = this.generateUniqueFileName(this.selectedFile.name)
        const key = `${this.selectedFolder}/${uniqueFileName}`

        // For large files, use multipart upload
        if (this.selectedFile.size > 100 * 1024 * 1024) { // 100MB threshold
          await this.multipartUpload(key)
        } else {
          await this.simpleUpload(key)
        }

        // Upload successful
        this.uploadedFile = {
          name: this.selectedFile.name,
          key: key,
          url: `https://${this.awsConfig.bucket}.s3.${this.awsConfig.region}.amazonaws.com/${key}`
        }

        this.isUploading = false
        this.selectedFile = null
        
        // Emit success event
        this.$emit('upload-success', this.uploadedFile)

      } catch (error) {
        this.isUploading = false
        
        if (error.name === 'AbortError') {
          this.errorMessage = 'Upload was cancelled'
        } else {
          this.errorMessage = error.message || 'Upload failed. Please try again.'
        }
        
        console.error('Upload error:', error)
        this.$emit('upload-error', error)
      }
    },

    async simpleUpload(key) {
      return new Promise((resolve, reject) => {
        const uploadParams = {
          Key: key,
          Body: this.selectedFile,
          ContentType: this.selectedFile.type,
          ACL: 'public-read'
        }

        const upload = this.s3.upload(uploadParams)

        // Track progress
        upload.on('httpUploadProgress', (progress) => {
          this.uploadProgress = Math.round((progress.loaded * 100) / progress.total)
          
          // Calculate upload speed
          const elapsed = (Date.now() - this.uploadStartTime) / 1000
          const speed = progress.loaded / elapsed
          this.uploadSpeed = this.formatSpeed(speed)
        })

        // Handle cancellation
        this.abortController.signal.addEventListener('abort', () => {
          upload.abort()
          reject(new Error('Upload cancelled'))
        })

        upload.send((error, data) => {
          if (error) {
            reject(error)
          } else {
            resolve(data)
          }
        })
      })
    },

    async multipartUpload(key) {
      const chunkSize = 10 * 1024 * 1024 // 10MB chunks
      const totalChunks = Math.ceil(this.selectedFile.size / chunkSize)
      let uploadedBytes = 0

      // Initialize multipart upload
      const createParams = {
        Key: key,
        ContentType: this.selectedFile.type,
        ACL: 'public-read'
      }

      const multipart = await this.s3.createMultipartUpload(createParams).promise()
      const uploadId = multipart.UploadId
      const parts = []

      try {
        // Upload each part
        for (let i = 0; i < totalChunks; i++) {
          if (this.abortController.signal.aborted) {
            throw new Error('Upload cancelled')
          }

          const start = i * chunkSize
          const end = Math.min(start + chunkSize, this.selectedFile.size)
          const chunk = this.selectedFile.slice(start, end)

          const partParams = {
            Key: key,
            PartNumber: i + 1,
            UploadId: uploadId,
            Body: chunk
          }

          const part = await this.s3.uploadPart(partParams).promise()
          parts.push({
            ETag: part.ETag,
            PartNumber: i + 1
          })

          uploadedBytes += chunk.size
          this.uploadProgress = Math.round((uploadedBytes * 100) / this.selectedFile.size)

          // Calculate upload speed
          const elapsed = (Date.now() - this.uploadStartTime) / 1000
          const speed = uploadedBytes / elapsed
          this.uploadSpeed = this.formatSpeed(speed)
        }

        // Complete multipart upload
        const completeParams = {
          Key: key,
          UploadId: uploadId,
          MultipartUpload: {
            Parts: parts.sort((a, b) => a.PartNumber - b.PartNumber)
          }
        }

        await this.s3.completeMultipartUpload(completeParams).promise()

      } catch (error) {
        // Abort multipart upload on error
        await this.s3.abortMultipartUpload({
          Key: key,
          UploadId: uploadId
        }).promise()
        throw error
      }
    },

    cancelUpload() {
      if (this.abortController) {
        this.abortController.abort()
      }
      this.isUploading = false
      this.uploadProgress = 0
    },

    clearSelection() {
      this.selectedFile = null
      this.$refs.fileInput.value = ''
    },

    resetUpload() {
      this.selectedFile = null
      this.uploadedFile = null
      this.uploadProgress = 0
      this.clearError()
      this.$refs.fileInput.value = ''
    },

    clearError() {
      this.errorMessage = ''
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    formatSpeed(bytesPerSecond) {
      const mbps = bytesPerSecond / (1024 * 1024)
      return mbps.toFixed(2) + ' MB/s'
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        alert('URL copied to clipboard!')
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        alert('URL copied to clipboard!')
      })
    }
  }
}
</script>

<style scoped>
.video-upload-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

.upload-area {
  border: 2px dashed #ddd;
  border-radius: 10px;
  padding: 40px;
  text-align: center;
  transition: all 0.3s ease;
}

.upload-area.dragging {
  border-color: #007bff;
  background-color: #f8f9fa;
}

.upload-zone {
  cursor: pointer;
  padding: 40px 20px;
}

.upload-zone:hover {
  background-color: #f8f9fa;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.upload-hint {
  color: #666;
  font-size: 14px;
  margin-top: 10px;
}

.file-selected {
  padding: 20px;
}

.file-info {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.file-info h4 {
  margin: 0 0 10px 0;
  color: #333;
}

.file-info p {
  margin: 5px 0;
  color: #666;
}

.folder-selection {
  margin-bottom: 20px;
}

.folder-selection label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
}

.folder-selection select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.upload-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.btn-upload {
  background: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
}

.btn-upload:hover:not(:disabled) {
  background: #0056b3;
}

.btn-upload:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.btn-cancel {
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
}

.btn-cancel:hover {
  background: #545b62;
}

.upload-progress {
  padding: 20px;
}

.progress-info h4 {
  margin-bottom: 15px;
}

.progress-bar {
  width: 100%;
  height: 20px;
  background: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  margin: 15px 0;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff, #0056b3);
  transition: width 0.3s ease;
}

.upload-success {
  padding: 20px;
}

.success-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.url-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin: 10px 0;
  font-family: monospace;
  font-size: 12px;
}

.success-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 15px;
}

.btn-copy {
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-copy:hover {
  background: #1e7e34;
}

.btn-new {
  background: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-new:hover {
  background: #0056b3;
}

.error-message {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 15px 20px;
  border-radius: 6px;
  margin-top: 15px;
}

.btn-clear {
  background: #dc3545;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

.btn-clear:hover {
  background: #c82333;
}
</style>